/**
 * ShiftInput — steering-wheel / media-key shifting via the MediaSession API.
 *
 * On a Tesla, pressing the right scroll wheel sends play/pause to the active
 * media session; tilting it sends next/previous track. We keep a silent audio
 * element playing so our page *is* the active media session, then map those
 * actions to gear changes.
 *
 * The LEFT scroll wheel is the volume wheel. Three candidate ways it can reach a
 * web page (all implemented, all logged — confirm which fires on /#diag):
 *   1. wheel events. Inside the Tesla browser the left wheel scrolls the page,
 *      and a page that does not scroll still gets the event. This is how the
 *      other apps do it, and it is the one that needs no permission and no
 *      media session at all: roll up to change up, roll down to change down.
 *   2. volumechange on the keep-alive <audio> element (if Tesla adjusts the
 *      media element's volume): up → shift up, down → shift down, then the
 *      volume is snapped back to the 0.5 baseline so the next turn works.
 *   3. keydown with key AudioVolumeUp/AudioVolumeDown (hardware volume keys),
 *      and PageUp/PageDown, which is what a scroll wheel sends on some builds.
 *
 * A wheel roll only ever means a gear here: the dash does not scroll. If the
 * pointer is over something that CAN scroll (the HUD menu, a panel), the roll
 * is left alone and scrolls it.
 */
export interface ShiftHandlers {
  onShiftUp: () => void;
  onShiftDown: () => void;
  /** a press with no direction of its own (the Tesla's wheel button): the car's motion decides */
  onShiftAuto?: () => void;
  /** Raw action log for diagnostics. */
  onAction?: (action: string) => void;
}

const ACTIONS: MediaSessionAction[] = ['play', 'pause', 'nexttrack', 'previoustrack', 'seekforward', 'seekbackward', 'stop'];

export class ShiftInput {
  private audio: HTMLAudioElement | null = null;
  private lastAt = 0;
  private attached = false;
  private resettingVolume = false;
  private wheelAcc = 0;
  private wheelAt = 0;
  private static readonly VOL_BASELINE = 0.5;
  /** a Tesla wheel notch is one 'line' or ~100 px; ask for a real roll, not a graze */
  private static readonly WHEEL_STEP = 40;

  constructor(private readonly h: ShiftHandlers) {}

  static get supported(): boolean {
    return typeof navigator !== 'undefined' && 'mediaSession' in navigator;
  }

  /** Must be called from a user gesture (it starts the silent keep-alive audio). */
  async attach(title: string, artist: string): Promise<boolean> {
    if (this.attached) return true;
    // the wheel and the volume keys need no gesture and no media session: wire them
    // up first so shifting works even where MediaSession does not exist
    window.addEventListener('wheel', this.onWheel, { passive: false });
    window.addEventListener('keydown', this.onVolumeKey);
    this.attached = true;
    if (!ShiftInput.supported) return false;
    try {
      const el = document.createElement('audio');
      el.src = '/audio/system/silence-60.mp3';
      el.loop = true;
      el.preload = 'auto';
      (el as HTMLAudioElement & { playsInline?: boolean }).playsInline = true;
      el.volume = ShiftInput.VOL_BASELINE; // baseline so left-wheel volume turns are detectable both ways
      document.body.appendChild(el);
      this.audio = el;
      await el.play();

      const ms = navigator.mediaSession;
      try {
        ms.metadata = new MediaMetadata({ title, artist, album: 'ROARDRIVE' });
      } catch {
        /* MediaMetadata may be missing */
      }
      for (const action of ACTIONS) {
        try {
          ms.setActionHandler(action, () => this.onAction(action));
        } catch {
          /* unsupported action */
        }
      }
      ms.playbackState = 'playing';
      // LEFT wheel candidate 1: media-element volume nudges
      el.addEventListener('volumechange', () => {
        if (this.resettingVolume) return;
        const v = el.volume;
        const d = v - ShiftInput.VOL_BASELINE;
        if (Math.abs(d) < 0.005) return;
        this.h.onAction?.(`volumechange ${v.toFixed(2)} (${d > 0 ? '+' : '-'})`);
        this.fire(d > 0 ? 1 : -1);
        this.resettingVolume = true;
        el.volume = ShiftInput.VOL_BASELINE;
        setTimeout(() => (this.resettingVolume = false), 60);
      });
      return true;
    } catch (err) {
      this.h.onAction?.(`mediasession attach failed: ${err instanceof Error ? err.message : String(err)}`);
      return false;
    }
  }

  /** true when the roll belongs to something that actually scrolls */
  private overScrollable(target: EventTarget | null): boolean {
    let el = target instanceof Element ? target : null;
    while (el && el !== document.body) {
      const st = getComputedStyle(el);
      if ((st.overflowY === 'auto' || st.overflowY === 'scroll') && el.scrollHeight - el.clientHeight > 4) return true;
      el = el.parentElement;
    }
    return false;
  }

  /** LEFT wheel candidate 1: the roll itself. Up is up, like every sequential box. */
  private onWheel = (e: WheelEvent): void => {
    if (this.overScrollable(e.target)) return;
    const now = performance.now();
    if (now - this.wheelAt > 400) this.wheelAcc = 0;   // a new roll, not the tail of the last one
    this.wheelAt = now;
    // lines (deltaMode 1) and pages (2) come in small numbers; make them comparable to pixels
    const px = e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 100 : 1);
    this.wheelAcc += px;
    if (Math.abs(this.wheelAcc) < ShiftInput.WHEEL_STEP) return;
    const dir: 1 | -1 = this.wheelAcc < 0 ? 1 : -1;
    this.wheelAcc = 0;
    this.h.onAction?.(`wheel ${px > 0 ? '+' : '-'}${Math.abs(Math.round(px))}`);
    this.fire(dir);
    if (e.cancelable) e.preventDefault();
  };

  private onVolumeKey = (e: KeyboardEvent): void => {
    if (e.key === 'PageUp') {
      this.h.onAction?.('key PageUp');
      this.fire(1);
      e.preventDefault();
      return;
    }
    if (e.key === 'PageDown') {
      this.h.onAction?.('key PageDown');
      this.fire(-1);
      e.preventDefault();
      return;
    }
    if (e.key === 'AudioVolumeUp' || e.key === 'VolumeUp') {
      this.h.onAction?.('key AudioVolumeUp');
      this.fire(1);
      e.preventDefault();
    } else if (e.key === 'AudioVolumeDown' || e.key === 'VolumeDown') {
      this.h.onAction?.('key AudioVolumeDown');
      this.fire(-1);
      e.preventDefault();
    }
  };

  private fire(dir: 1 | -1 | 0): void {
    const now = performance.now();
    if (now - this.lastAt < 220) return; // debounce double events
    this.lastAt = now;
    if (dir === 0) { (this.h.onShiftAuto ?? this.h.onShiftUp)(); return; }
    if (dir > 0) this.h.onShiftUp();
    else this.h.onShiftDown();
  }

  private onAction(action: MediaSessionAction): void {
    this.h.onAction?.(action);
    // keep the keep-alive playing no matter what the OS asked for
    void this.audio?.play().catch(() => undefined);
    try {
      navigator.mediaSession.playbackState = 'playing';
    } catch {
      /* ignore */
    }
    switch (action) {
      // a PRESS carries no direction: up while pulling, down while slowing (Marc)
      case 'play':
      case 'pause':
        this.fire(0);
        break;
      case 'nexttrack':
      case 'seekforward':
        this.fire(1);
        break;
      case 'previoustrack':
      case 'seekbackward':
        this.fire(-1);
        break;
      default:
        break;
    }
  }

  detach(): void {
    if (!this.attached) return;
    this.attached = false;
    try {
      for (const a of ACTIONS) navigator.mediaSession.setActionHandler(a, null);
    } catch {
      /* ignore */
    }
    window.removeEventListener('wheel', this.onWheel);
    window.removeEventListener('keydown', this.onVolumeKey);
    this.audio?.pause();
    this.audio?.remove();
    this.audio = null;
  }
}
